import * as React from "react";

import { useRequiredContext } from "replo-utils/react/context";

/**
 * NOTE (Yuxin, 2026-01-30): Simplified context that provides stub values.
 * We no longer use Daytona - the Cloudflare sandbox URL is managed by
 * useSandboxPreviewBaseUrl() in the sandboxNavigationStore.
 */

type NextDevServerContextType = {
  logs: Array<{ date: Date; message: string }>;
  url: string | undefined;
  pageStatusMap: Record<
    string,
    { timestamp: number; status: number; hasImportError?: boolean }
  >;
  refreshEpoch: number;
  status: number;
  invalidatePageImportErrors: (pageRoutes: string[]) => void;
  error: unknown | null;
  mountAdditionalFilesStatus: "notStarted" | "mounting" | "compiled";
  removeFromPageStatusMap: (pageRoute: string) => void;
  clearPageStatusMap: () => void;
  incrementRefreshEpoch: () => void;
  isServerSandboxLoading: boolean;
  isSyncPending: boolean;
  sandboxClientSideErrors: Map<
    string,
    { message: string; stack?: string; componentStack?: string }
  >;
  clearSandboxClientSideError: (pageRoute: string) => void;
};

const NextDevServerContext = React.createContext<
  NextDevServerContextType | undefined
>(undefined);
NextDevServerContext.displayName = "NextDevServerContext";

// Stub values - Cloudflare sandbox is now used instead of Daytona
const stubContextValue: NextDevServerContextType = {
  logs: [],
  url: undefined, // Not used - PageRenderer uses useSandboxPreviewBaseUrl() directly
  pageStatusMap: {},
  refreshEpoch: 0,
  status: 7, // Ready
  invalidatePageImportErrors: () => {},
  error: null,
  mountAdditionalFilesStatus: "compiled",
  removeFromPageStatusMap: () => {},
  clearPageStatusMap: () => {},
  incrementRefreshEpoch: () => {},
  isServerSandboxLoading: false,
  isSyncPending: false,
  sandboxClientSideErrors: new Map(),
  clearSandboxClientSideError: () => {},
};

const NextDevServerProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  return (
    <NextDevServerContext.Provider value={stubContextValue}>
      {children}
    </NextDevServerContext.Provider>
  );
};

function useNextDevServer() {
  return useRequiredContext(NextDevServerContext);
}

export { NextDevServerContext, NextDevServerProvider, useNextDevServer };
